// Le dessin d'une case, avec les cinq regles de docs/REGLES.md :
//   R1 la couleur vient de la zone   R2 le sens vient des voisines
//   R3 les decors allonges se tracent en branches depuis le centre
//   R4 une seule matiere de sol par zone   R5 le mur contraste avec le sol
import { dessinerTuile, dessinerInterieur } from './atlas.js';

// ═══ QUELLE PLANCHE, QUELLES TUILES, PAR ZONE ═══
// Coordonnees relevees dans l'atelier .mockup/atlas.html (col, ligne).
// ⚠️ R5 : le mur doit contraster avec le sol. Plutot que de chercher indefiniment la
// bonne paire de tuiles, on assombrit le sol et on eclaircit le mur : le contraste est
// alors REGLABLE, et il ne depend pas du hasard des planches.
const ZONES = {
  hub:       { feuille: 'ville', sol: [13, 1], mur: [5, 8],   ombreSol: '#171a21', voileSol: 'rgba(0,0,0,0.30)', voileMur: 'rgba(255,255,255,0.10)' },
  pods:      { feuille: 'monde', sol: [5, 1],  mur: [8, 13],  ombreSol: '#16280f', voileSol: 'rgba(0,0,0,0.06)', voileMur: 'rgba(0,0,0,0.12)' },
  workloads: { feuille: 'monde', sol: [6, 1],  mur: [14, 13], ombreSol: '#1d1811', voileSol: 'rgba(0,0,0,0.22)', voileMur: 'rgba(255,255,255,0.06)' },
  network:   { feuille: 'ville', sol: [12, 1], mur: [4, 8],   ombreSol: '#080e1a', voileSol: 'rgba(10,20,45,0.42)', voileMur: 'rgba(60,140,255,0.10)' },
  sanctum:   { feuille: 'monde', sol: [7, 2],  mur: [6, 14],  ombreSol: '#140e1f', voileSol: 'rgba(40,20,60,0.30)', voileMur: 'rgba(120,80,180,0.14)' },
};

// Ce qui fait bloc. Tout le reste se traverse ou se pose sur le sol.
const MURS = new Set('MINPVW#');

// Decors poses sur le sol : une tuile de la planche de la zone.
// ⚠️ Chaque entree a ete verifiee EN REGARDANT la planche (atelier : build/candidats.html).
// Les precedentes avaient ete choisies aux coordonnees : le « buisson » etait un bout de
// haie, la « rune » un morceau de tapis, le « brasier » un piquet. Une tuile qu'on ne
// sait pas nommer est une tuile a rechoisir (regle R9 : la forme dit la fonction).
const POSES = {
  T: [15, 9, 'monde'],   // arbre entier, tronc compris
  S: [23, 10, 'monde'],  // buisson a baies
  J: [17, 7, 'monde'],   // torche murale allumee
  K: [55, 7, 'monde'],   // brasier allume
  Q: [1, 21, 'monde'],   // dalle de cristal
  C: [32, 10, 'monde'],  // baie de serveur
  D: [41, 7, 'monde'],   // porte
  L: [1, 14, 'ville'],   // lampadaire de rue
  g: [5, 1, 'monde'],    // herbe (interieurs)
};
const CHEMINS = { p: [6, 0] };                 // chemin de terre
const DALLES = { '=': [17, 1], '+': [16, 1] }; // dalles de la rotonde
const LIGNES = { c: 'voie', r: 'rambarde', b: 'passerelle', G: 'herse' };
const VIDE = new Set('~');

// R4 : une seule matiere de sol par zone. Ce tableau la NOMME, ce qui permet au
// garde-fou de distinguer « ce caractere est le sol de la zone » de « ce caractere
// n'est declare nulle part et se dessine donc comme du sol, sans le dire ».
const SOLS = { hub: 'm', pods: ',', workloads: 'e', network: 'n', sanctum: 'a' };

// Tirage stable par case : deux voisines different, rien ne scintille (R4).
const bruit = (x, y) => {
  let h = (x * 73856093) ^ (y * 19349663);
  h = (h ^ (h >>> 13)) * 1274126177;
  return ((h ^ (h >>> 16)) >>> 0) % 1000;
};

const estMur = (c) => c === undefined || MURS.has(c);

/**
 * Dessine une case.
 * @param vois {h,b,g,d,ax,ay} les quatre voisines et les coordonnees absolues
 */
function dessinerCase(ctx, px, py, ch, vois, zone, tick, nA, T) {
  const Z = ZONES[zone] || ZONES.hub;
  const v = vois || {};
  const b = bruit(v.ax | 0, v.ay | 0);
  const F = Z.feuille;

  if (ch === undefined || ch === '#') { ctx.fillStyle = Z.ombreSol; ctx.fillRect(px, py, T, T); return; }

  // ── le vide : rien dessous, et des donnees qui filent
  if (VIDE.has(ch)) {
    ctx.fillStyle = zone === 'network' ? '#06090f' : '#123a5e';
    ctx.fillRect(px, py, T, T);
    if (zone === 'network') {
      const d = (tick * 0.6 + b) % 70;
      if (d < 12) { ctx.fillStyle = `rgba(84,160,255,${0.30 - d * 0.024})`; ctx.fillRect(px + 5 + b % 20, py + Math.floor(d * 2.4), 2, 6); }
    } else {
      ctx.fillStyle = 'rgba(90,170,230,0.25)';
      ctx.fillRect(px, py + 8 + Math.sin(tick * 0.05 + px * 0.05) * 2, T, 5);
    }
    if (nA > 0) { ctx.fillStyle = `rgba(10,10,50,${nA * 0.22})`; ctx.fillRect(px, py, T, T); }
    return;
  }

  // ── R4 : une seule matiere de sol, la variation est un voile, pas une autre tuile
  // ⚠️ Les tuiles de dallage ont un joint peint sur leur bord : repetees telles quelles,
  // ces joints s'alignent et fabriquent une grille reguliere sur tout l'ecran. Deborder
  // d'un pixel ne suffisait pas — le joint etait toujours dessine, juste decale. On lit
  // donc l'INTERIEUR de la tuile, joint exclu.
  dessinerInterieur(ctx, F, Z.sol[0], Z.sol[1], px, py, T, 1);
  if (Z.voileSol) { ctx.fillStyle = Z.voileSol; ctx.fillRect(px, py, T, T); }
  if (b % 6 === 0) { ctx.fillStyle = 'rgba(255,255,255,0.035)'; ctx.fillRect(px + b % 12, py + b % 14, 11, 8); }
  if (b % 9 === 0) { ctx.fillStyle = 'rgba(0,0,0,0.055)'; ctx.fillRect(px + b % 16, py + b % 10, 9, 7); }

  // ── liseré au bord du vide : sinon on tombe sans voir le bord
  if (VIDE.has(v.h) || VIDE.has(v.b) || VIDE.has(v.g) || VIDE.has(v.d)) {
    ctx.fillStyle = 'rgba(84,160,255,0.55)';
    if (VIDE.has(v.h)) ctx.fillRect(px, py, T, 2);
    if (VIDE.has(v.b)) ctx.fillRect(px, py + T - 2, T, 2);
    if (VIDE.has(v.g)) ctx.fillRect(px, py, 2, T);
    if (VIDE.has(v.d)) ctx.fillRect(px + T - 2, py, 2, T);
  }

  // ── ombre portee du mur d'au-dessus (R5)
  if (estMur(v.h) && v.h !== undefined) { ctx.fillStyle = 'rgba(0,0,0,0.32)'; ctx.fillRect(px, py, T, 7); }

  // ── R5 : LES MURS sont des volumes, pas des couleurs
  if (MURS.has(ch)) {
    dessinerTuile(ctx, F, Z.mur[0], Z.mur[1], px, py, T);
    if (Z.voileMur) { ctx.fillStyle = Z.voileMur; ctx.fillRect(px, py, T, T); }
    const mg = estMur(v.g), md = estMur(v.d), mh = estMur(v.h), mb = estMur(v.b);
    if (!mh) {
      ctx.fillStyle = 'rgba(255,255,255,0.16)'; ctx.fillRect(px, py, T, 5);
      ctx.fillStyle = 'rgba(255,255,255,0.07)'; ctx.fillRect(px, py + 5, T, 4);
    }
    if (!mb) { ctx.fillStyle = 'rgba(0,0,0,0.42)'; ctx.fillRect(px, py + T - 4, T, 4); }
    if (!mg) { ctx.fillStyle = 'rgba(0,0,0,0.26)'; ctx.fillRect(px, py, 3, T); }
    if (!md) { ctx.fillStyle = 'rgba(0,0,0,0.26)'; ctx.fillRect(px + T - 3, py, 3, T); }
    ctx.fillStyle = 'rgba(0,0,0,0.30)';
    if (mh && mg && !estMur(v.hg)) ctx.fillRect(px, py, 4, 4);
    if (mh && md && !estMur(v.hd)) ctx.fillRect(px + T - 4, py, 4, 4);
    if (ch === 'N') { const g = Math.sin(tick * 0.03 + px * 0.05 + py * 0.05) * 0.3 + 0.7; ctx.fillStyle = `rgba(60,140,255,${g * 0.12})`; ctx.fillRect(px, py, T, T); }
    if (nA > 0) { ctx.fillStyle = `rgba(10,10,50,${nA * 0.22})`; ctx.fillRect(px, py, T, T); }
    return;
  }

  // ── LES CHEMINS : bord marque la ou ils mordent sur le sol voisin
  if (CHEMINS[ch]) {
    // Un passage use, pas un trait peint : le bord s'estompe au lieu d'etre souligne,
    // et quelques cailloux cassent la ligne droite. Avant, deux traits noirs au cordeau
    // le decoupaient case par case et le chemin se lisait comme un marquage au sol.
    const t = CHEMINS[ch];
    dessinerTuile(ctx, F, t[0], t[1], px - 1, py - 1, T + 2);
    const bordu = (bx, by, bw, bh, sens) => {
      const g = sens === 'v' ? ctx.createLinearGradient(bx, 0, bx + bw, 0) : ctx.createLinearGradient(0, by, 0, by + bh);
      g.addColorStop(0, 'rgba(0,0,0,0.20)'); g.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = g; ctx.fillRect(bx, by, bw, bh);
    };
    if (v.g !== ch) bordu(px, py, 5, T, 'v');
    if (v.d !== ch) { const g = ctx.createLinearGradient(px + T, 0, px + T - 5, 0); g.addColorStop(0, 'rgba(0,0,0,0.20)'); g.addColorStop(1, 'rgba(0,0,0,0)'); ctx.fillStyle = g; ctx.fillRect(px + T - 5, py, 5, T); }
    if (v.h !== ch) bordu(px, py, T, 5, 'h');
    if (v.b !== ch) { const g = ctx.createLinearGradient(0, py + T, 0, py + T - 5); g.addColorStop(0, 'rgba(0,0,0,0.20)'); g.addColorStop(1, 'rgba(0,0,0,0)'); ctx.fillStyle = g; ctx.fillRect(px, py + T - 5, T, 5); }
    ctx.fillStyle = 'rgba(0,0,0,0.10)';
    ctx.fillRect(px + b % 20, py + (b >> 3) % 22, 4, 3);
    ctx.fillRect(px + (b >> 5) % 24, py + (b >> 2) % 24, 3, 2);
  } else if (DALLES[ch]) {
    const t = DALLES[ch];
    dessinerTuile(ctx, F, t[0], t[1], px, py, T);
    ctx.fillStyle = 'rgba(0,0,0,0.12)'; ctx.fillRect(px, py, T, 1); ctx.fillRect(px, py, 1, T);
  } else if (LIGNES[ch]) {
    // ── R2 + R3 : le sens vient des voisines, le trace part du centre
    const type = LIGNES[ch];
    const G = v.g === ch, Dr = v.d === ch, H = v.h === ch, B = v.b === ch;
    const ce = T / 2, h1 = ce - 7, h2 = ce + 2;
    const br = (c2, bx, by, bw, bh) => { if (c2) ctx.fillRect(px + bx, py + by, bw, bh); };
    if (type === 'voie') {
      // Le revetement d'abord : sans lui, le marquage flotte sur le sol de la zone et
      // ne se lit plus comme une route (c'etait le cas : deux traits jaunes sur du gris).
      dessinerTuile(ctx, 'ville', 11, 19, px - 1, py - 1, T + 2);
      const horiz = G || Dr, vert = H || B;
      // Le marquage suit le sens de la voie, et s'interrompt aux carrefours.
      ctx.fillStyle = 'rgba(240,200,90,0.75)';
      if (vert && !horiz) { for (let o = 2; o < T - 4; o += 10) ctx.fillRect(px + T / 2 - 1, py + o, 2, 6); }
      if (horiz && !vert) { for (let o = 2; o < T - 4; o += 10) ctx.fillRect(px + o, py + T / 2 - 1, 6, 2); }
      // Bordure la ou la route mord sur le sol voisin : une route a un trottoir.
      ctx.fillStyle = 'rgba(0,0,0,0.28)';
      if (!G) ctx.fillRect(px, py, 2, T);
      if (!Dr) ctx.fillRect(px + T - 2, py, 2, T);
      if (!H) ctx.fillRect(px, py, T, 2);
      if (!B) ctx.fillRect(px, py + T - 2, T, 2);
    } else {
      const clair = type === 'passerelle' ? '#a08b62' : (type === 'herse' ? '#8a8f9c' : '#9aa7bc');
      const fonce = type === 'passerelle' ? '#6d5c40' : (type === 'herse' ? '#5a5f6c' : '#77839a');
      ctx.fillStyle = 'rgba(0,0,0,0.16)';
      br(G, 0, ce + 8, ce, 3); br(Dr, ce, ce + 8, ce, 3); br(H, ce + 8, 0, 3, ce); br(B, ce + 8, ce, 3, ce);
      ctx.fillStyle = clair;
      br(G, 0, h1, ce, 4); br(Dr, ce, h1, ce, 4); br(H, h1, 0, 4, ce); br(B, h1, ce, 4, ce);
      ctx.fillStyle = fonce;
      br(G, 0, h2, ce, 2); br(Dr, ce, h2, ce, 2); br(H, h2, 0, 2, ce); br(B, h2, ce, 2, ce);
      // Un poteau a chaque case donnait un pointille. Une barriere a des poteaux
      // espaces, et un montant a chaque extremite ou changement de direction (R2).
      const droit = (G && Dr && !H && !B) || (H && B && !G && !Dr);
      const jalon = !droit || ((v.ax | 0) + (v.ay | 0)) % 3 === 0;
      if (jalon) {
        ctx.fillStyle = 'rgba(0,0,0,0.25)'; ctx.fillRect(px + h1 + 1, py + h1 + 2, 6, 13);
        ctx.fillStyle = '#4d5464'; ctx.fillRect(px + h1, py + h1, 5, 13);
        ctx.fillStyle = clair; ctx.fillRect(px + h1, py + h1, 5, 3);
      }
      if (!G && !Dr && !H && !B) { ctx.fillStyle = '#4d5464'; ctx.fillRect(px + h1, py + h1, 5, 13); }
    }
  } else if (ch === 'H') { // lave
    ctx.fillStyle = '#3a0a00'; ctx.fillRect(px, py, T, T);
    const lv = Math.sin(tick * 0.06 + px * 0.07 + py * 0.1) * 0.3 + 0.7;
    ctx.fillStyle = `rgba(255,60,0,${lv * 0.32})`; ctx.fillRect(px, py, T, T);
  } else if (POSES[ch]) {
    // ── R6 : rien ne flotte
    const t = POSES[ch];
    ctx.fillStyle = 'rgba(0,0,0,0.22)';
    ctx.beginPath(); ctx.ellipse(px + T / 2, py + T - 5, T * 0.31, T * 0.12, 0, 0, 6.3); ctx.fill();
    dessinerTuile(ctx, t[2] || F, t[0], t[1], px, py - 4, T);
    if (ch === 'C') { // l'ecran de la baie clignote : on voit qu'elle fonctionne
      const led = Math.sin(tick * 0.07 + px * 0.1) > 0;
      ctx.fillStyle = led ? 'rgba(57,255,20,0.85)' : 'rgba(57,255,20,0.25)';
      ctx.fillRect(px + 9, py + 3, 4, 3);
    }
  }

  // ⚠️ Aucun halo ici : dessine tuile par tuile, il est a moitie recouvert par les
  // tuiles suivantes et se lit comme un dome gris. Les halos sont poses en une seule
  // passe apres toutes les tuiles (voir dessinerHalos).
  if (nA > 0) { ctx.fillStyle = `rgba(10,10,50,${nA * 0.22})`; ctx.fillRect(px, py, T, T); }
}

// Les sources de lumiere, en UNE passe apres toutes les tuiles : sinon chaque halo est
// tronque par la tuile dessinee juste apres.
const LUMIERES = new Set('LJKC');
function dessinerHalos(ctx, carte, camX, camY, VW, VH, nA, T) {
  if (nA <= 0.15) return;
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  for (let ty = 0; ty <= VH; ty++) for (let tx = 0; tx <= VW; tx++) {
    const ch = carte[camY + ty]?.[camX + tx];
    if (!LUMIERES.has(ch)) continue;
    const px = tx * T + T / 2, py = ty * T + T / 2;
    const g = ctx.createRadialGradient(px, py, 2, px, py, T * 2.2);
    g.addColorStop(0, `rgba(255,190,90,${nA * 0.22})`);
    g.addColorStop(1, 'rgba(255,190,90,0)');
    ctx.fillStyle = g;
    ctx.beginPath(); ctx.arc(px, py, T * 2.2, 0, Math.PI * 2); ctx.fill();
  }
  ctx.restore();
}

export { dessinerCase, dessinerHalos, ZONES, MURS, SOLS, POSES, CHEMINS, DALLES, LIGNES, VIDE, bruit };
